import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, Loader2, CheckCircle2, AlertTriangle,
  PlayCircle, Users, ShieldAlert, Info, GitMerge,
} from 'lucide-react';
import { privatiFileMergeApi } from '../lib/api';
import { toast } from 'sonner';

const PrivatiFileMergePage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyState, setApplyState] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');

  const doPreview = async () => {
    if (!file) { toast.error('Seleziona un file Excel'); return; }
    setUploading(true); setPreview(null); setApplyState(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await privatiFileMergeApi.preview(fd);
      setPreview(data);
      toast.success(
        `File: ${data.groups_in_file} gruppi · OK ${data.resolved_ok} · Skip ${data.skipped} · ${data.total_duplicates_to_archive} duplicati PRIVATI da archiviare`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore preview');
    } finally { setUploading(false); }
  };

  const doApply = async () => {
    if (!preview) return;
    if (confirmText.trim().toUpperCase() !== 'UNISCI DUPLICATI PRIVATI') {
      toast.error("Scrivi esattamente: UNISCI DUPLICATI PRIVATI");
      return;
    }
    if (!window.confirm(`Sei sicuro?\n\nArchivierai ${preview.total_duplicates_to_archive} lavoratori PRIVATI e sposterai le loro formazioni/visite/DPI sui rispettivi lavoratori delle aziende reali.\n\nBackup automatico. Rollback tramite manifesto.`)) return;
    setApplying(true);
    setApplyState({ state: 'starting', phase: 'starting', progress: 0, total: preview.resolved_ok });
    try {
      const { data } = await privatiFileMergeApi.apply({
        token: preview.token,
        confirmation_text: confirmText.trim(),
      });
      const opId = data.opId;
      const poll = setInterval(async () => {
        try {
          const { data: st } = await privatiFileMergeApi.status(opId);
          setApplyState(st);
          if (st.state === 'done') {
            clearInterval(poll);
            toast.success(`✅ ${st.duplicates_archived} duplicati archiviati · ${st.linked_updated_totals?.formazione || 0} formazioni spostate · opId ${opId}`);
            setApplying(false);
          } else if (st.state === 'error') {
            clearInterval(poll);
            toast.error(`Errore: ${st.error}`);
            setApplying(false);
          }
        } catch (err) { clearInterval(poll); setApplying(false); }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore apply');
      setApplying(false);
    }
  };

  const filteredRows = useMemo(() => {
    if (!preview) return [];
    if (filterStatus === 'ALL') return preview.rows;
    return preview.rows.filter((r) => r.status === filterStatus);
  }, [preview, filterStatus]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900" data-testid="pfm-back">
            <ArrowLeft size={20} />
          </button>
          <div className="p-2.5 rounded-xl bg-rose-100 text-rose-700"><GitMerge size={22} /></div>
          <div>
            <h1 className="text-2xl font-bold text-[#023F80]">Unione Duplicati PRIVATI da File</h1>
            <p className="text-sm text-slate-500">
              Carica il file Excel con l'elenco dei duplicati. Per ogni gruppo <strong>PRIVATI + AZIENDA REALE</strong>,
              archivio i PRIVATI e sposto le loro formazioni/visite/DPI sul worker dell'azienda reale (preserva la storia).
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border-l-4 border-rose-400 bg-rose-50/50 p-4 text-sm text-rose-900 flex items-start gap-2">
        <ShieldAlert size={16} className="mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">Operazione irreversibile senza backup</p>
          <p className="text-rose-800/80 text-xs">
            Verrà creato un backup ($out) di workers/formazione/visite/dpi/attachments prima della scrittura.
            Rollback via manifest opId. Confermazione testuale richiesta.
          </p>
        </div>
      </div>

      {/* Uploader */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <p className="text-sm font-semibold text-slate-700 mb-1">1. Carica il file Excel dei duplicati</p>
        <p className="text-xs text-slate-500 mb-3">
          Formato atteso: foglio "Nomi da verificare" con colonne Tipo, Nome normalizzato, Nome i, Azienda i, CF i (fino a 3 lavoratori per gruppo).
        </p>
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept=".xlsx,.xlsm,.xlsb"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setApplyState(null); }}
            data-testid="pfm-file-input"
            className="text-xs file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
          />
          <button
            onClick={doPreview}
            disabled={!file || uploading}
            data-testid="pfm-preview-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#023F80] text-white disabled:opacity-60"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Analizzo…' : 'Anteprima'}
          </button>
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl p-4 bg-slate-100">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Gruppi nel file</div>
              <div className="text-2xl font-extrabold text-slate-900">{preview.groups_in_file}</div>
            </div>
            <div className="rounded-xl p-4 bg-emerald-100">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-800">Risolti OK</div>
              <div className="text-2xl font-extrabold text-emerald-900">{preview.resolved_ok}</div>
            </div>
            <div className="rounded-xl p-4 bg-amber-100">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-800">Skippati</div>
              <div className="text-2xl font-extrabold text-amber-900">{preview.skipped}</div>
            </div>
            <div className="rounded-xl p-4 bg-rose-100">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-rose-800">PRIVATI da archiviare</div>
              <div className="text-2xl font-extrabold text-rose-900">{preview.total_duplicates_to_archive}</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
            {['ALL', 'ok', 'skip'].map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                data-testid={`pfm-filter-${s}`}
                className={`text-xs px-2.5 py-1 rounded-full border ${
                  filterStatus === s ? 'border-[#023F80] bg-indigo-50 text-[#023F80] font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {s === 'ALL' ? `Tutti · ${preview.rows.length}` : s === 'ok' ? `OK · ${preview.resolved_ok}` : `Skip · ${preview.skipped}`}
              </button>
            ))}
            <span className="text-xs text-slate-500 ml-auto">{filteredRows.length} righe visibili</span>
          </div>

          <div className="overflow-x-auto max-h-[55vh] overflow-y-auto border border-slate-100 rounded-xl">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-3 py-2 text-left">Riga</th>
                  <th className="px-3 py-2 text-left">Stato</th>
                  <th className="px-3 py-2 text-left">Nome</th>
                  <th className="px-3 py-2 text-left">Principale (azienda reale)</th>
                  <th className="px-3 py-2 text-left">Duplicati PRIVATI</th>
                  <th className="px-3 py-2 text-left">Nota</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.row} className={`border-b border-slate-50 ${r.status !== 'ok' ? 'bg-amber-50/40' : ''}`}>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{r.row}</td>
                    <td className="px-3 py-2">
                      {r.status === 'ok' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                          <CheckCircle2 size={10} /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                          <AlertTriangle size={10} /> SKIP
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-semibold">{r.nome_norm}</td>
                    <td className="px-3 py-2">
                      {r.principal ? (
                        <div>
                          <div className="font-semibold">{r.principal.lastName} {r.principal.firstName}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{r.principal.fiscalCode || 'CF —'}</div>
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      {r.duplicates.length > 0 ? r.duplicates.map((d) => (
                        <div key={d.id} className="text-[11px]">
                          · {d.lastName} {d.firstName} <span className="text-slate-400 font-mono">({d.fiscalCode || 'no CF'})</span>
                        </div>
                      )) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-[10px]">{r.note || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Confirmation + Apply */}
          {preview.resolved_ok > 0 && (
            <div className="pt-3 border-t border-slate-100 space-y-2">
              <label className="block text-xs font-semibold text-slate-700">
                Digita <code className="bg-slate-100 px-1.5 py-0.5 rounded">UNISCI DUPLICATI PRIVATI</code> per abilitare l'esecuzione
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="UNISCI DUPLICATI PRIVATI"
                  data-testid="pfm-confirm-input"
                  className="flex-1 min-w-[280px] px-3 py-2 text-sm border border-slate-200 rounded-lg font-mono"
                />
                <button
                  onClick={doApply}
                  disabled={applying || confirmText.trim().toUpperCase() !== 'UNISCI DUPLICATI PRIVATI'}
                  data-testid="pfm-apply-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-rose-600 text-white disabled:opacity-60"
                >
                  {applying ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
                  {applying ? 'Applico…' : `Applica ${preview.total_duplicates_to_archive} archiviazioni`}
                </button>
              </div>
              {applyState && (
                <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-slate-700">Stato:</span>
                    <span className="text-slate-600">{applyState.phase || applyState.state}</span>
                    {applyState.state === 'done' && <CheckCircle2 size={14} className="text-emerald-600" />}
                  </div>
                  {applyState.total > 0 && (
                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-2 bg-rose-500 transition-all"
                           style={{ width: `${Math.round(((applyState.progress || 0) / applyState.total) * 100)}%` }} />
                    </div>
                  )}
                  <p className="text-slate-500 mt-1">
                    {applyState.progress || 0} / {applyState.total || 0}
                    {applyState.state === 'done' && ` · archiviati ${applyState.duplicates_archived || 0} · formazioni spostate ${applyState.linked_updated_totals?.formazione || 0}`}
                  </p>
                  {applyState.state === 'done' && applyState.backup_collections && (
                    <div className="mt-2 text-[10px] text-slate-500">
                      <strong>Backup rollback:</strong>
                      <ul className="ml-3 mt-0.5 space-y-0.5">
                        {Object.entries(applyState.backup_collections).map(([k, v]) => (
                          <li key={k}><code>{v}</code></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PrivatiFileMergePage;
